import { useEffect, useRef, useState } from 'react';
import { useClipStore, Message } from '../stores/clipStore';
import { decrypt, base64ToArrayBuffer } from '../crypto';
import { CopyIcon, CheckIcon, XMarkIcon } from './icons';
import { useI18n } from '../i18n';
import { ChunkedFileAssembler } from '../crypto/chunked';

export default function MessageList() {
  const messages = useClipStore((state) => state.messages);
  const mySenderId = useClipStore((state) => state.mySenderId);
  const bottomRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <svg className="w-12 h-12 mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p>{t.messageList.empty}</p>
          <p className="text-sm">{t.messageList.emptyHint}</p>
        </div>
      ) : (
        messages.map((message) => (
          <MessageItem
            key={message.id}
            message={message}
            isMe={message.sender === mySenderId}
          />
        ))
      )}
      <div ref={bottomRef} />
    </div>
  );
}

function MessageItem({
  message,
  isMe,
}: {
  message: Message;
  isMe: boolean;
}) {
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 ${
          isMe
            ? 'bg-blue-600 text-white rounded-br-md'
            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
        }`}
      >
        {message.type === 'text' ? (
          <TextMessage content={message.content || ''} isMe={isMe} />
        ) : message.type === 'image' || message.type === 'image_start' ? (
          <EncryptedImage message={message} />
        ) : null}
        <span
          className={`text-xs mt-1 block ${isMe ? 'text-blue-200' : 'text-gray-400'}`}
        >
          {formatTime(message.timestamp)}
        </span>
      </div>
    </div>
  );
}

function TextMessage({ content, isMe }: { content: string; isMe: boolean }) {
  const [copied, setCopied] = useState(false);
  const { t } = useI18n();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <p className="text-sm whitespace-pre-wrap break-words pr-8">{content}</p>
      <button
        onClick={handleCopy}
        className={`absolute top-0 right-0 p-1 rounded transition-all opacity-0 group-hover:opacity-100 ${
          isMe ? 'hover:bg-blue-500 text-blue-100' : 'hover:bg-gray-100 text-gray-500'
        }`}
        title={t.messageList.copy}
      >
        {copied ? <CheckIcon className="w-4 h-4" /> : <CopyIcon className="w-4 h-4" />}
      </button>
    </div>
  );
}

function ImagePreviewModal({ imageUrl, filename, onClose }: { imageUrl: string; filename?: string; onClose: () => void }) {
  const { t } = useI18n();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={handleBackdropClick}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
        title={t.messageList.closeEsc}
      >
        <XMarkIcon className="w-8 h-8" />
      </button>
      <img
        src={imageUrl}
        alt={filename || 'Preview'}
        className="max-w-full max-h-[90vh] object-contain rounded-lg"
      />
    </div>
  );
}

// Component-level Map to store assemblers for chunked images
const imageAssemblers = new Map<string, ChunkedFileAssembler>();
const imageUrls = new Map<string, string>();

function EncryptedImage({ message }: { message: Message }) {
  const cryptoKey = useClipStore((state) => state.cryptoKey);
  const messages = useClipStore((state) => state.messages);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    if (!cryptoKey) {
      setLoading(false);
      return;
    }

    // Handle legacy direct image type
    if (message.type === 'image' && message.data) {
      const decryptAndDisplay = async () => {
        try {
          setLoading(true);
          setError(false);

          // Decrypt the base64 data
          const decryptedBase64 = await decrypt(cryptoKey, message.data!);

          // Convert base64 to ArrayBuffer
          const buffer = base64ToArrayBuffer(decryptedBase64);

          // Create Blob and URL
          const blob = new Blob([buffer], { type: message.mimeType || 'image/jpeg' });
          const url = URL.createObjectURL(blob);

          setImageUrl(url);
        } catch (e) {
          console.error('Failed to decrypt image:', e);
          setError(true);
        } finally {
          setLoading(false);
        }
      };

      decryptAndDisplay();
    }

    // Handle new chunked protocol: image_start
    if (message.type === 'image_start' && message.fileId) {
      const assembleFromChunks = async () => {
        try {
          setLoading(true);
          setError(false);

          const fileId = message.fileId!;

          // Check if we already have a completed image URL
          const existingUrl = imageUrls.get(fileId);
          if (existingUrl) {
            setImageUrl(existingUrl);
            setLoading(false);
            return;
          }

          // Get or create assembler
          let assembler = imageAssemblers.get(fileId);
          if (!assembler) {
            const manifest: import('../crypto/chunked').FileManifest = {
              id: fileId,
              filename: message.filename || 'image.jpg',
              mimeType: message.mimeType || 'image/jpeg',
              totalChunks: message.totalChunks || 0,
              totalSize: message.totalSize || 0,
            };
            assembler = new ChunkedFileAssembler(manifest, (received, total) => {
              setProgress(Math.round((received / total) * 100));
            });
            imageAssemblers.set(fileId, assembler);
          }

          // Collect all chunks for this file from messages
          const fileChunks = messages.filter(
            (m) => m.type === 'image_chunk' && m.fileId === fileId && m.data
          );

          // Sort chunks by index to ensure correct order
          fileChunks.sort((a, b) => (a.index || 0) - (b.index || 0));

          // Add chunks to assembler
          for (const chunk of fileChunks) {
            if (chunk.index !== undefined && chunk.data) {
              try {
                // Decrypt the chunk data
                const decryptedBase64 = await decrypt(cryptoKey, chunk.data);
                const buffer = base64ToArrayBuffer(decryptedBase64);
                assembler.addChunk(chunk.index, buffer);
              } catch (e) {
                console.warn('Failed to decrypt/add chunk:', e);
              }
            }
          }

          // Check if we have image_complete message
          const completeMsg = messages.find(
            (m) => m.type === 'image_complete' && m.fileId === fileId
          );

          if (completeMsg && assembler.isComplete()) {
            const url = assembler.createObjectURL();
            imageUrls.set(fileId, url);
            setImageUrl(url);
            setLoading(false);
          } else {
            // Still receiving chunks, show progress but keep loading state
            setProgress(assembler.getReceivedCount() / (message.totalChunks || 1) * 100);
            // Don't set loading to false - we want to keep showing the progress
          }
        } catch (e) {
          console.error('Failed to assemble chunked image:', e);
          setError(true);
          setLoading(false);
        }
      };

      assembleFromChunks();
    }

    // Cleanup URL on unmount
    return () => {
      // Note: We don't cleanup here to keep the image available
    };
  }, [message, message.data, message.type, message.fileId, message.mimeType, cryptoKey, messages]);

  if (loading) {
    return (
      <div className="w-48 h-32 bg-gray-100 rounded-lg flex flex-col items-center justify-center">
        {message.type === 'image_start' && progress > 0 ? (
          <>
            <div className="text-sm text-gray-500 mb-2">{Math.round(progress)}%</div>
            <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </>
        ) : (
          <svg className="animate-spin h-5 w-5 text-gray-400" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-48 h-32 bg-red-50 rounded-lg flex items-center justify-center text-red-500 text-sm">
        {t.messageList.failedToLoad}
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div className="w-48 h-32 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">
        {t.messageList.noImageData}
      </div>
    );
  }

  return (
    <>
      <img
        src={imageUrl}
        alt={message.filename || 'Shared image'}
        className="max-w-full max-h-96 rounded-lg object-contain cursor-pointer hover:opacity-90 transition-opacity"
        onClick={() => setShowPreview(true)}
        title={t.messageList.clickToEnlarge}
      />
      {showPreview && (
        <ImagePreviewModal
          imageUrl={imageUrl}
          filename={message.filename}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
}

